import type { ISubscriber } from '@/Structures/subscribers/types';

/**
 * A valid billing contact for one unit, with the occupancy window clipped to the
 * billing period. Windows are property-local YYYY-MM-DD strings (lexically
 * comparable); windowEndIso is the tenant's INCLUSIVE last billed day.
 */
export type BillingContactWindow = {
  subscriberId: string;
  windowStartIso: string;
  windowEndIso: string;
  /** True when the clipped window does not span the whole billing period. */
  isPartialPeriod: boolean;
};

/**
 * Map of unit_id -> valid billing contacts (with clipped occupancy windows) for a
 * billing period. Mirrors backend BillingManager.get_valid_billing_contacts under
 * the occupancy-driven model (revised 2026-06-02):
 *   1. Include every tenant whose occupancy window overlaps the billing period
 *      (regardless of is_billing_contact flag)
 *   2. Group co-tenants whose windows overlap each other within the period
 *   3. Per group, dedup: prefer is_billing_contact=true tenants; tiebreak by
 *      earliest move-in then subscriber_id
 * Contacts per unit are ordered by window start (sequential tenants render in
 * move-in order). Inputs must already be property-local ISO dates: bill
 * start_time/end_time are stored as midnight in the property's timezone and
 * move_in/move_out arrive as property-local YYYY-MM-DD strings, so comparing in
 * UTC milliseconds would shift by the tz offset and break 1-day occupancies at
 * the period edge. move_out_date is the tenant's inclusive last day of lease.
 */
export const computeValidBillingContactWindows = (
  subscribers: ISubscriber[],
  billingStartIso: string,
  billingEndIso: string,
): Map<string, BillingContactWindow[]> => {
  const result = new Map<string, BillingContactWindow[]>();

  type Candidate = {
    subscriberId: string;
    isBillingContact: boolean;
    moveInIso: string; // empty string when move_in is null (sorts earliest)
    windowStartIso: string;
    windowEndIso: string;
  };

  // Step 1: filter to overlapping-occupancy candidates, grouped by unit
  const byUnit = new Map<string, Candidate[]>();
  for (const sub of subscribers) {
    if (!sub.id) continue;
    for (const us of sub.unit_subscriptions || []) {
      const moveInIso = us.move_in_date || '';
      const moveOutInclusiveIso = us.move_out_date || '';
      const windowStartIso = moveInIso && moveInIso > billingStartIso ? moveInIso : billingStartIso;
      const windowEndIso =
        moveOutInclusiveIso && moveOutInclusiveIso < billingEndIso
          ? moveOutInclusiveIso
          : billingEndIso;
      // Use < (not <=) so a 1-day occupancy (windowStart == windowEnd) is still counted.
      if (windowEndIso < windowStartIso) continue;

      if (!byUnit.has(us.unit_id)) byUnit.set(us.unit_id, []);
      byUnit.get(us.unit_id)!.push({
        subscriberId: sub.id,
        isBillingContact: !!us.is_billing_contact,
        moveInIso,
        windowStartIso,
        windowEndIso,
      });
    }
  }

  // Step 2 + 3: per unit, build overlap groups and pick a winner per group
  byUnit.forEach((candidates, unitId) => {
    // Build connected components of overlapping windows
    const groups: Candidate[][] = [];
    for (const c of candidates) {
      const overlappingGroupIdxs: number[] = [];
      groups.forEach((g, idx) => {
        // Two windows overlap when max(start) <= min(end) in ISO date space.
        if (
          g.some((other) => {
            const latestStart =
              c.windowStartIso > other.windowStartIso ? c.windowStartIso : other.windowStartIso;
            const earliestEnd =
              c.windowEndIso < other.windowEndIso ? c.windowEndIso : other.windowEndIso;
            return latestStart <= earliestEnd;
          })
        ) {
          overlappingGroupIdxs.push(idx);
        }
      });
      if (overlappingGroupIdxs.length === 0) {
        groups.push([c]);
      } else {
        // Merge any groups c bridges into the first one, then drop the rest
        const target = groups[overlappingGroupIdxs[0]];
        target.push(c);
        for (let i = overlappingGroupIdxs.length - 1; i >= 1; i--) {
          target.push(...groups[overlappingGroupIdxs[i]]);
          groups.splice(overlappingGroupIdxs[i], 1);
        }
      }
    }

    const winners: Candidate[] = [];
    for (const group of groups) {
      if (group.length === 1) {
        winners.push(group[0]);
        continue;
      }
      const flagged = group.filter((c) => c.isBillingContact);
      const pool = flagged.length > 0 ? flagged : group;
      pool.sort((a, b) => {
        // Empty move_in (null) sorts earliest (matches backend default).
        if (a.moveInIso !== b.moveInIso) return a.moveInIso < b.moveInIso ? -1 : 1;
        return a.subscriberId.localeCompare(b.subscriberId);
      });
      winners.push(pool[0]);
    }

    winners.sort((a, b) => {
      if (a.windowStartIso !== b.windowStartIso) {
        return a.windowStartIso < b.windowStartIso ? -1 : 1;
      }
      return a.subscriberId.localeCompare(b.subscriberId);
    });
    result.set(
      unitId,
      winners.map((c) => ({
        subscriberId: c.subscriberId,
        windowStartIso: c.windowStartIso,
        windowEndIso: c.windowEndIso,
        isPartialPeriod: c.windowStartIso > billingStartIso || c.windowEndIso < billingEndIso,
      })),
    );
  });

  return result;
};

export type OwnerShareSummary = {
  /** Sum of persisted invoice amounts across units that have persisted invoices. */
  invoicedTotalDollars: number;
  /** Full-period computed total for those same units. */
  computedTotalDollars: number;
  /** computed minus invoiced; positive when the owner absorbs unbilled usage. */
  ownerShareDollars: number;
};

// Per-unit gaps at or below one cent are documented rounding drift between the
// PDF line-item math and the live breakdown math (see the reconciliation note in
// customer-lambdas ResponseMapper.py), never a real owner share. 0.015 rather
// than 0.01 so float error on a one-cent difference cannot tip it over.
const PER_UNIT_ROUNDING_DRIFT_DOLLARS = 0.015;

/**
 * Reconcile persisted (as-issued) invoice amounts against the live full-period
 * computation, per unit. Only units with at least one persisted invoice amount
 * participate, and each unit's computed total counts once regardless of how many
 * invoices it split into (mirrors the CSV export's reconciliation note math).
 * The owner share sums only per-unit gaps large enough to be occupancy-driven;
 * cent-level rounding drift is ignored. Returns null when no invoice has a
 * persisted amount yet, or when every gap is rounding drift.
 */
export const computeOwnerShareSummary = (
  units: Array<{
    id: string;
    unit_cost_meter?: number | null;
    unit_estimated_cost?: number | null;
    unit_cost?: number | null;
  }>,
  invoices: Array<{ id?: string | null; unitId?: string | null }>,
  persistedAmountCentsByInvoiceId: Map<string, number>,
): OwnerShareSummary | null => {
  let invoicedTotalDollars = 0;
  let computedTotalDollars = 0;
  let ownerShareDollars = 0;

  for (const unit of units) {
    const persistedCents = invoices
      .filter((inv) => inv.unitId === unit.id && inv.id != null)
      .map((inv) => persistedAmountCentsByInvoiceId.get(inv.id as string))
      .filter((cents): cents is number => cents !== undefined);
    if (persistedCents.length === 0) continue;

    const unitInvoicedDollars = persistedCents.reduce((sum, cents) => sum + cents, 0) / 100;
    const unitComputedDollars =
      (unit.unit_cost_meter || 0) + (unit.unit_estimated_cost || 0) + (unit.unit_cost || 0);
    invoicedTotalDollars += unitInvoicedDollars;
    computedTotalDollars += unitComputedDollars;
    const unitGapDollars = unitComputedDollars - unitInvoicedDollars;
    if (unitGapDollars > PER_UNIT_ROUNDING_DRIFT_DOLLARS) {
      ownerShareDollars += unitGapDollars;
    }
  }

  if (ownerShareDollars <= 0) return null;
  return {
    invoicedTotalDollars,
    computedTotalDollars,
    ownerShareDollars,
  };
};
